import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { stampProof } from "@/lib/stampProof";
import { acknowledgeOrders } from "@/hooks/useOrderNotifications";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShoppingBag, Package, Truck, CheckCircle2, XCircle, KeyRound, Camera, X,
  MapPin, Phone, User2, Loader2, RefreshCw, Search, Undo2, IndianRupee,
} from "lucide-react";

const STATUS_META = {
  pending:      { label: "Pending",      color: "bg-slate-100 text-slate-700",   dot: "bg-slate-400" },
  confirmed:    { label: "Confirmed",    color: "bg-blue-50 text-blue-700",      dot: "bg-blue-500" },
  dispatched:   { label: "Dispatched",   color: "bg-amber-50 text-amber-700",    dot: "bg-amber-500" },
  awaiting_otp: { label: "Awaiting OTP", color: "bg-fuchsia-50 text-fuchsia-700", dot: "bg-fuchsia-500" },
  delivered:    { label: "Delivered",    color: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  cancelled:    { label: "Cancelled",    color: "bg-red-50 text-red-700",        dot: "bg-red-500" },
  refunded:     { label: "Refunded",     color: "bg-fuchsia-50 text-fuchsia-700", dot: "bg-fuchsia-500" },
};

const IST_STAMP = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      timeZone: "Asia/Kolkata", hour12: false,
    });
  } catch { return "—"; }
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  // OTP verification dialog (opens when the owner clicks "Collect OTP" on a
  // dispatched/awaiting_otp order — the customer reads out their 4-digit code)
  const [otpOrder, setOtpOrder] = useState(null);
  const [otpInput, setOtpInput] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [proofPhoto, setProofPhoto] = useState("");
  const [proofCompressing, setProofCompressing] = useState(false);
  // Owner-side zoom viewer for the already-uploaded doorstep photo shown on
  // delivered orders. Kept separate from `proofPhoto` (which is the freshly
  // captured JPEG the agent hasn't yet submitted).
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const cameraInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/orders");
      setOrders(data || []);
    } catch {
      toast.error("Could not load orders");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Mark all currently-visible orders as seen so the sidebar badge clears.
    acknowledgeOrders();
  }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (tab !== "all" && o.status !== tab) return false;
      if (!s) return true;
      return (
        (o.customer_name || "").toLowerCase().includes(s) ||
        (o.customer_mobile || "").includes(s) ||
        (o.pin_code || "").includes(s) ||
        (o.id || "").toLowerCase().includes(s)
      );
    });
  }, [orders, tab, q]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/marketplace/orders/${id}`, { status });
      toast.success(`Order marked ${status}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally { setBusyId(null); }
  };

  // Kick off the OTP flow: hits the employee-side /arrive endpoint (which
  // generates a fresh 4-digit code the customer sees in their app), then
  // opens the verification dialog for the owner to enter it.
  const openOtpDialog = async (o) => {
    setBusyId(o.id);
    try {
      // Only call /arrive if the OTP hasn't been generated yet.
      if (o.status !== "awaiting_otp") {
        await api.post(`/employee/deliveries/${o.id}/arrive`);
      }
      setOtpInput("");
      setProofPhoto("");
      setOtpOrder(o);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate OTP");
    } finally { setBusyId(null); }
  };

  const capturePhoto = async (file) => {
    if (!file) return;
    if (!otpOrder) return;
    setProofCompressing(true);
    try {
      const { dataUrl } = await compressImage(file, { maxDimension: 1200, quality: 0.75 });
      const stamped = await stampProof(dataUrl, {
        timestamp: new Date().toISOString(),
        pin: otpOrder.pin_code || "",
        orderId: otpOrder.id,
      });
      setProofPhoto(stamped);
    } catch {
      toast.error("Could not read that photo. Try again.");
    } finally {
      setProofCompressing(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otpOrder) return;
    const clean = otpInput.replace(/\D/g, "");
    if (clean.length !== 4) {
      toast.error("Enter the 4-digit code from the customer's app.");
      return;
    }
    if (!proofPhoto) {
      toast.error("Snap a doorstep photo first — it's required to close the delivery.");
      return;
    }
    setOtpBusy(true);
    try {
      await api.post(`/employee/deliveries/${otpOrder.id}/verify-otp`, {
        otp: clean,
        proof_photo: proofPhoto,
      });
      toast.success("Delivery completed");
      setOtpOrder(null);
      setOtpInput("");
      setProofPhoto("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP did not match");
    } finally { setOtpBusy(false); }
  };

  const resendOtp = async () => {
    if (!otpOrder) return;
    setOtpBusy(true);
    try {
      await api.post(`/employee/deliveries/${otpOrder.id}/arrive`);
      toast.success("Fresh OTP sent to the customer");
      setOtpInput("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend OTP");
    } finally { setOtpBusy(false); }
  };

  const refundOrder = async (o) => {
    const ok = window.confirm(
      `Refund ₹${Number(o.total).toLocaleString("en-IN")} for order ${o.id.slice(0, 8)}?\n\n` +
      `This will call Razorpay to reverse the payment and mark the order as refunded. This cannot be undone.`
    );
    if (!ok) return;
    setBusyId(o.id);
    try {
      const { data } = await api.post(`/marketplace/orders/${o.id}/refund`);
      toast.success(`Refund initiated · ${data.razorpay_refund_id || ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    } finally { setBusyId(null); }
  };

  const counts = useMemo(() => {
    const c = { all: orders.length };
    Object.keys(STATUS_META).forEach((k) => { c[k] = orders.filter((o) => o.status === k).length; });
    return c;
  }, [orders]);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-orders">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Marketplace</div>
          <h1 className="font-heading text-3xl font-bold mt-2 flex items-center gap-2">
            <ShoppingBag className="w-7 h-7" /> Orders
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Incoming customer orders. Confirm, dispatch, and mark delivered as you fulfil them.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="orders-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {[["pending", "New"], ["confirmed", "Confirmed"], ["dispatched", "Dispatched"], ["awaiting_otp", "Awaiting OTP"], ["delivered", "Delivered"], ["cancelled", "Cancelled"], ["refunded", "Refunded"], ["all", "All"]].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={
              "px-3 py-1.5 text-sm rounded-full border transition " +
              (tab === k ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
            }
            data-testid={`orders-tab-${k}`}
          >
            {lbl}
            <span className={"ml-1.5 mono text-[10px] " + (tab === k ? "opacity-80" : "text-slate-400")}>{counts[k] || 0}</span>
          </button>
        ))}
      </div>

      <div className="mb-3 relative max-w-md">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input placeholder="Search name, mobile, PIN, order id…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="orders-search" />
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-8 text-center text-slate-500 text-sm">
            <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            {orders.length === 0 ? "No orders yet." : "No orders match the current filter."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.pending;
            return (
              <Card key={o.id} className="border-slate-200 shadow-none" data-testid={`order-card-${o.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-slate-400">Order · {o.id.slice(0, 8)}</div>
                      <div className="mono text-xs text-slate-500 mt-0.5">{IST_STAMP(o.created_at)}</div>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm">
                        <span className="flex items-center gap-1"><User2 className="w-3.5 h-3.5 text-slate-400" /> <b>{o.customer_name}</b></span>
                        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" /> <a href={`tel:${o.customer_mobile}`} className="hover:underline">{o.customer_mobile}</a></span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400" /> PIN {o.pin_code} · {o.area}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap max-w-lg">{o.delivery_address}</div>
                      {o.assigned_employee_name && (
                        <div
                          className="mono text-[10px] mt-1 inline-block bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded"
                          data-testid={`order-assigned-${o.id}`}
                        >
                          🛵 Assigned to {o.assigned_employee_name}
                        </div>
                      )}
                      {o.notes && <div className="text-xs italic text-slate-500 mt-1 max-w-lg">Note: {o.notes}</div>}
                    </div>
                    <span className={"mono text-[10px] px-2 py-0.5 rounded uppercase inline-flex items-center gap-1 " + meta.color}>
                      <span className={"w-1.5 h-1.5 rounded-full " + meta.dot} /> {meta.label}
                    </span>
                  </div>

                  {/* Doorstep proof photo — captured by the delivery agent at
                      OTP verify. Owner-side surface (same as the customer sees
                      on their /shop/track and /shop/orders pages) so disputes
                      about "was it really delivered?" can be settled in-app. */}
                  {o.status === "delivered" && o.delivery_proof_photo && (
                    <button
                      type="button"
                      onClick={() => setZoomPhoto(o.delivery_proof_photo)}
                      className="w-full mb-2 rounded-md overflow-hidden border border-emerald-200 relative group focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid={`order-proof-${o.id}`}
                    >
                      <img
                        src={o.delivery_proof_photo}
                        alt="Doorstep delivery proof"
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent flex items-end justify-between p-2">
                        <span className="mono text-[10px] uppercase tracking-widest text-white bg-emerald-600 px-2 py-0.5 rounded shadow flex items-center gap-1">
                          <Camera className="w-3 h-3" /> Doorstep proof
                        </span>
                        <span className="text-white/90 text-[11px] group-hover:underline">Tap to zoom</span>
                      </div>
                    </button>
                  )}

                  {/* Items */}
                  <div className="border-t border-slate-100 pt-2">
                    <ul className="space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          {it.image ? <img src={it.image} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 bg-slate-100 rounded" />}
                          <span className="flex-1 truncate">{it.name}</span>
                          <span className="mono text-xs text-slate-500">×{it.quantity}</span>
                          <span className="mono w-24 text-right">₹{Number(it.line_total).toLocaleString("en-IN")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-slate-100 mt-2 pt-2 flex flex-wrap justify-between items-center gap-3">
                    <div className="mono text-xs text-slate-600">
                      Subtotal ₹{Number(o.subtotal).toLocaleString("en-IN")} + Shipping ₹{Number(o.shipping_charge).toLocaleString("en-IN")}
                      <b className="text-slate-900 ml-2">Total ₹{Number(o.total).toLocaleString("en-IN")}</b>
                      {o.payment_status === "paid" && (
                        <span className="ml-2 mono text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase inline-flex items-center gap-1">
                          <IndianRupee className="w-3 h-3" /> Paid
                        </span>
                      )}
                      {o.payment_status === "refunded" && (
                        <span className="ml-2 mono text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 uppercase">
                          Refunded {o.razorpay_refund_id ? `· ${o.razorpay_refund_id.slice(-6)}` : ""}
                        </span>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex flex-wrap gap-1.5">
                      {o.status === "pending" && (
                        <>
                          <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => setStatus(o.id, "confirmed")} disabled={busyId === o.id} data-testid={`order-confirm-${o.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-red-600 hover:text-red-700" onClick={() => setStatus(o.id, "cancelled")} disabled={busyId === o.id} data-testid={`order-cancel-${o.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                      {o.status === "confirmed" && (
                        <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" onClick={() => setStatus(o.id, "dispatched")} disabled={busyId === o.id} data-testid={`order-dispatch-${o.id}`}>
                          <Truck className="w-4 h-4 mr-1" /> Dispatch
                        </Button>
                      )}
                      {o.status === "dispatched" && (
                        <Button
                          size="sm"
                          className="h-8 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => openOtpDialog(o)}
                          disabled={busyId === o.id}
                          data-testid={`order-collect-otp-${o.id}`}
                        >
                          {busyId === o.id
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <KeyRound className="w-4 h-4 mr-1" />}
                          Collect OTP
                        </Button>
                      )}
                      {o.status === "awaiting_otp" && (
                        <Button
                          size="sm"
                          className="h-8 bg-fuchsia-600 hover:bg-fuchsia-700"
                          onClick={() => openOtpDialog(o)}
                          disabled={busyId === o.id}
                          data-testid={`order-enter-otp-${o.id}`}
                        >
                          <KeyRound className="w-4 h-4 mr-1" />
                          Enter OTP
                        </Button>
                      )}
                      {o.payment_status === "paid" && o.status !== "refunded" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-fuchsia-700 hover:text-fuchsia-800 border-fuchsia-200 hover:border-fuchsia-300"
                          onClick={() => refundOrder(o)}
                          disabled={busyId === o.id}
                          data-testid={`order-refund-${o.id}`}
                        >
                          {busyId === o.id
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <Undo2 className="w-4 h-4 mr-1" />}
                          Refund
                        </Button>
                      )}
                      {(o.status === "delivered" || o.status === "cancelled" || o.status === "refunded") && o.payment_status !== "paid" && (
                        <span className="mono text-[10px] text-slate-400">no actions</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Full-screen zoom for the doorstep proof photo. Owner clicks the
          Delivered card thumbnail → this opens. */}
      {zoomPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoomPhoto(null)}
          data-testid="order-proof-zoom"
        >
          <button
            type="button"
            onClick={() => setZoomPhoto(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={zoomPhoto}
            alt="Doorstep delivery proof"
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* OTP verification dialog — the customer reads out the 4-digit code
          shown in their /shop/orders page. Delivery is only marked complete
          once that code is submitted here AND a doorstep proof photo is
          attached (required by policy). */}
      <Dialog
        open={!!otpOrder}
        onOpenChange={(v) => { if (!v) { setOtpOrder(null); setProofPhoto(""); } }}
      >
        <DialogContent className="max-w-sm" data-testid="order-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-fuchsia-700" />
              Confirm delivery with OTP
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              A 4-digit code has appeared on{" "}
              <span className="font-medium text-slate-700">
                {otpOrder?.customer_name || "the customer"}
              </span>
              &apos;s <span className="font-medium">Order history</span> screen. Ask
              them to read it out, snap a doorstep photo, and submit.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={verifyOtp} className="space-y-4 py-2">
            {/* Doorstep photo — required. Uses `capture=environment` so mobile
                browsers open the back camera directly. */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Doorstep photo <span className="text-red-500">*</span></span>
                {proofPhoto && (
                  <button
                    type="button"
                    onClick={() => setProofPhoto("")}
                    className="text-[11px] text-slate-500 hover:text-red-600 flex items-center gap-1"
                    data-testid="order-otp-photo-clear"
                  >
                    <X className="w-3 h-3" /> Retake
                  </button>
                )}
              </Label>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => capturePhoto(e.target.files?.[0])}
                data-testid="order-otp-photo-input"
              />
              {proofPhoto ? (
                <div className="relative rounded-md overflow-hidden border border-emerald-200">
                  <img
                    src={proofPhoto}
                    alt="Doorstep proof"
                    className="w-full h-40 object-cover"
                    data-testid="order-otp-photo-preview"
                  />
                  <div className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] mono uppercase tracking-wider px-2 py-0.5 rounded shadow">
                    ✓ Photo captured
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={proofCompressing}
                  className="w-full h-32 border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/30 rounded-md flex flex-col items-center justify-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors disabled:opacity-50"
                  data-testid="order-otp-photo-capture"
                >
                  {proofCompressing ? (
                    <><Loader2 className="w-6 h-6 animate-spin" /><span className="text-xs">Processing…</span></>
                  ) : (
                    <>
                      <Camera className="w-7 h-7" />
                      <span className="text-xs font-medium">Snap doorstep photo</span>
                      <span className="text-[10px] text-slate-400">Required · opens camera</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="otp-input">4-digit code <span className="text-red-500">*</span></Label>
              <Input
                id="otp-input"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="mono text-2xl text-center tracking-[0.4em] font-bold h-14"
                required
                data-testid="order-otp-input"
              />
              <p className="text-[11px] text-slate-500">
                Wrong code? The customer&apos;s screen refreshes every 10s. Tap{" "}
                <b>Resend</b> to issue a fresh code if the old one expired.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resendOtp}
                disabled={otpBusy}
                data-testid="order-otp-resend"
              >
                Resend
              </Button>
              <Button
                type="submit"
                disabled={otpBusy || otpInput.length !== 4 || !proofPhoto}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="order-otp-verify"
              >
                {otpBusy ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Verifying…</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Mark delivered</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
